import { useApolloClient } from '@apollo/client';
import { isNil, isUndefined } from 'lodash';
import { useCallback } from 'react';
import { getChannel, listV2 } from '@shared/api/queries/channel';
import { API } from '@shared/types/api';
import { useCurrentUserId } from '@shared/util/hooks';
import { useCreateChannel } from './create/hooks';

export function useGetDmChannel(): (
  userId: number,
) => Promise<API.ListChannelsQuery['listChannels'][number] | undefined> {
  const currentUserId = useCurrentUserId();
  const client = useApolloClient();
  return useCallback(
    async (userId: number) => {
      const numMembers = currentUserId === userId ? 1 : 2;
      const { data } = await client.query<API.ListChannelsQuery, API.ListChannelsQueryVariables>({
        query: listV2,
        variables: { input: { type: 'DM' } },
        fetchPolicy: 'network-only',
      });
      const channels = data?.listChannels;
      return findDmChannel(channels, numMembers, userId);
    },
    [currentUserId, client],
  );
}

export function useGetOrCreateDmChannel(): (
  userId: number,
) => Promise<Omit<API.ChannelCreateFragment, 'workflowTemplateId'> | undefined> {
  const currentUserId = useCurrentUserId();
  const getDmChannel = useGetDmChannel();
  const createChannel = useCreateChannel();

  return useCallback(
    async (userId: number) => {
      const channel = await getDmChannel(userId);
      if (!isNil(channel)) {
        return channel;
      }
      const userIds = currentUserId === userId ? [] : [userId];
      return createChannel({ type: 'DM', name: '' }, userIds);
    },
    [createChannel, currentUserId, getDmChannel],
  );
}

export function useCreateMissingDmChannels(): (userIds: number[]) => Promise<void> {
  const createChannel = useCreateChannel();
  const client = useApolloClient();
  return useCallback(
    async (userIds: number[]) => {
      const { data, loading } = await client.query<
        API.ListChannelsQuery,
        API.ListChannelsQueryVariables
      >({
        query: listV2,
        variables: { input: { type: 'DM' } },
        fetchPolicy: 'network-only',
      });
      const channels = data?.listChannels;
      await Promise.all(
        userIds.map(async (userId: number) => {
          if (loading) {
            return;
          }
          const channel = findDmChannel(channels, 2, userId);
          if (!isUndefined(channel)) {
            return;
          }
          await createChannel(
            {
              type: 'DM',
              name: '',
            },
            [userId],
          );
        }),
      );
    },
    [createChannel, client],
  );
}

function findDmChannel(
  channels: API.ListChannelsQuery['listChannels'][number][],
  numMembers: number,
  userId: number,
): API.ListChannelsQuery['listChannels'][number] | undefined {
  if (isNil(channels)) {
    return undefined;
  }
  return channels.find(c => {
    const users = c.channelMemberships.map(m => m.user);
    return (
      c.type === 'DM' &&
      c.channelMemberships.length === numMembers &&
      users.some(u => u.id === userId)
    );
  });
}

export function useGetChannel() {
  const client = useApolloClient();

  return useCallback(
    async (channelId: number) => {
      const { data, loading, error } = await client.query<
        API.ChannelQuery,
        API.ChannelQueryVariables
      >({
        query: getChannel,
        variables: { id: channelId },
      });
      const channel = data?.channel;
      return { channel, loading, error };
    },
    [client],
  );
}
